import { c as _c } from "react/compiler-runtime";
import { type RechartsLabelProps as LabelProps, RechartsXAxis as XAxis, type RechartsXAxisProps as XAxisProps, RechartsYAxis as YAxis, type RechartsYAxisProps as YAxisProps } from '@precis-digital/kurama/recharts';
import { type ReactElement } from 'react';
import { chartHasAxis } from '../constants';
import { useChartElementContext } from '../hooks/chartElementContext';
import type { ElementDescriptionMetric, ElementsDescriptionsMap, SecondAxisDetails, XYAxisProps } from '../internalTypes';
import type { ChartCardDefaults, ChartCardGridSize, ChartDataRow, ChartType, LayoutType } from '../types';
import { getAxisDescription } from '../utils/axis';
import { errorLocal, formatMetricValue } from '../utils/generics';
import { useChartCardDefaults } from '../hooks/useChartCardDefaults';
const getLabelProps = ({
  defaults,
  layout,
  ordinal,
  chartType,
  label
}: {
  defaults: ChartCardDefaults;
  layout: LayoutType;
  ordinal: number;
  chartType: ChartType;
  label: string;
}): LabelProps => {
  let labelPosition: LabelProps['position'] | undefined;
  let labelPositionCorrection: Pick<LabelProps, 'offset' | 'dx' | 'dy'> | undefined;
  if (layout === 'vertical') {
    labelPosition = 'insideBottom';
  } else {
    labelPosition = ordinal === 1 && chartType !== 'scatter' ? 'insideTopRight' : 'insideTopLeft';
    labelPositionCorrection = {
      offset: -defaults.axis.space.verticalLabelOffset,
      dx: ordinal === 1 && chartType !== 'scatter' ? -defaults.axis.space.verticalLabelOffset : defaults.axis.space.verticalLabelOffset
    };
  }
  return {
    position: labelPosition,
    value: label,
    ...labelPositionCorrection,
    ...defaults.fonts.axisLabels
  };
};
interface SecondAxisProps<DataKey extends string, DataRow extends ChartDataRow<DataKey>> {
  layout: LayoutType;
  size: ChartCardGridSize;
  axisIndex: number;
  secondAxisDetails: SecondAxisDetails<DataKey, DataRow>;
  secondAxisDomains: Array<XYAxisProps['domain']>;
  elementsDescriptionsMap: ElementsDescriptionsMap<DataKey>;
}
const SecondAxis = t0 => {
  const $ = _c(61);
  const {
    layout,
    axisIndex,
    secondAxisDetails,
    secondAxisDomains,
    elementsDescriptionsMap
  } = t0;
  const defaults = useChartCardDefaults();
  const {
    chartAxisIdsAndDomains: t1
  } = useChartElementContext();
  const {
    xTicks,
    yTicks
  } = t1;
  const {
    chartType,
    axis,
    dataKeys,
    label,
    axisDrawing
  } = secondAxisDetails;
  if (!chartHasAxis[chartType]) {
    return null;
  }
  const domain = secondAxisDomains[axisIndex];
  let t2;
  if ($[0] !== axis || $[1] !== dataKeys || $[2] !== elementsDescriptionsMap) {
    t2 = getAxisDescription(axis, elementsDescriptionsMap, dataKeys);
    $[0] = axis;
    $[1] = dataKeys;
    $[2] = elementsDescriptionsMap;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const axisDescription = t2;
  if (axisDescription == null) {
    errorLocal(`useSecondAxis ${axisIndex + 1} getAxisDescription returned null`, {
      axis,
      elementsDescriptionsMap,
      dataKeys
    });
    return null;
  }
  let t3;
  if ($[4] !== axisDescription || $[5] !== dataKeys || $[6] !== defaults.axis.format) {
    t3 = value => formatMetricValue(dataKeys[0], axisDescription as ElementDescriptionMetric, value, defaults.axis.format);
    $[4] = axisDescription;
    $[5] = dataKeys;
    $[6] = defaults.axis.format;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const tickFormatter = t3;
  const t4 = chartType === "scatter" ? defaults.axis.yLine : false;
  let t5;
  if ($[8] !== defaults.fonts.axisTicks) {
    t5 = {
      ...defaults.fonts.axisTicks
    };
    $[8] = defaults.fonts.axisTicks;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== defaults.axis.space.yHorizontal || $[11] !== defaults.fonts.axisTicks || $[12] !== t4 || $[13] !== t5 || $[14] !== yTicks) {
    t6 = {
      axisLine: t4,
      width: defaults.axis.space.yHorizontal,
      ...defaults.fonts.axisTicks,
      tick: t5,
      tickCount: yTicks
    };
    $[10] = defaults.axis.space.yHorizontal;
    $[11] = defaults.fonts.axisTicks;
    $[12] = t4;
    $[13] = t5;
    $[14] = yTicks;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  const yAxisProps = t6;
  const t7 = chartType === "scatter" || layout === "vertical" ? defaults.axis.xLine : false;
  let t8;
  if ($[16] !== defaults.fonts.axisTicks) {
    t8 = {
      ...defaults.fonts.axisTicks
    };
    $[16] = defaults.fonts.axisTicks;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== defaults.axis.space.xStandardVertical || $[19] !== defaults.fonts.axisTicks || $[20] !== t7 || $[21] !== t8 || $[22] !== xTicks) {
    t9 = {
      axisLine: t7,
      height: defaults.axis.space.xStandardVertical,
      ...defaults.fonts.axisTicks,
      tick: t8,
      tickCount: xTicks
    };
    $[18] = defaults.axis.space.xStandardVertical;
    $[19] = defaults.fonts.axisTicks;
    $[20] = t7;
    $[21] = t8;
    $[22] = xTicks;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  const xAxisProps = t9;
  const t10 = domain != null;
  let t11;
  if ($[24] !== axisDrawing || $[25] !== domain || $[26] !== t10 || $[27] !== tickFormatter) {
    t11 = {
      scale: "auto",
      tickLine: false,
      tickFormatter,
      domain,
      allowDataOverflow: t10,
      ...axisDrawing
    };
    $[24] = axisDrawing;
    $[25] = domain;
    $[26] = t10;
    $[27] = tickFormatter;
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  const axisProps = t11;
  const ordinal = axisIndex + 1;
  let t12;
  if ($[29] !== chartType || $[30] !== defaults || $[31] !== label || $[32] !== layout || $[33] !== ordinal) {
    t12 = getLabelProps({
      defaults,
      layout,
      ordinal,
      chartType,
      label
    });
    $[29] = chartType;
    $[30] = defaults;
    $[31] = label;
    $[32] = layout;
    $[33] = ordinal;
    $[34] = t12;
  } else {
    t12 = $[34];
  }
  const labelProps = t12;
  const xAxisOrientation = ordinal === 1 ? "bottom" : "top";
  const yAxisOrientation = ordinal === 1 === (chartType !== "scatter") ? "right" : "left";
  if (chartType === "scatter") {
    if (layout === "vertical") {
      let t13;
      if ($[35] !== axis || $[36] !== axisProps || $[37] !== dataKeys[0] || $[38] !== labelProps || $[39] !== xAxisOrientation || $[40] !== xAxisProps) {
        t13 = <XAxis key={dataKeys[0]} dataKey={dataKeys[0]} type="number" {...axisProps} {...xAxisProps} xAxisId={axis} orientation={xAxisOrientation} label={labelProps} />;
        $[35] = axis;
        $[36] = axisProps;
        $[37] = dataKeys[0];
        $[38] = labelProps;
        $[39] = xAxisOrientation;
        $[40] = xAxisProps;
        $[41] = t13;
      } else {
        t13 = $[41];
      }
      return t13;
    } else {
      let t13;
      if ($[42] !== axis || $[43] !== axisProps || $[44] !== dataKeys[0] || $[45] !== labelProps || $[46] !== yAxisOrientation || $[47] !== yAxisProps) {
        t13 = <YAxis key={dataKeys[0]} dataKey={dataKeys[0]} type="number" {...axisProps} {...yAxisProps} yAxisId={axis} orientation={yAxisOrientation} label={labelProps} />;
        $[42] = axis;
        $[43] = axisProps;
        $[44] = dataKeys[0];
        $[45] = labelProps;
        $[46] = yAxisOrientation;
        $[47] = yAxisProps;
        $[48] = t13;
      } else {
        t13 = $[48];
      }
      return t13;
    }
  }
  if (layout === "vertical") {
    let t13;
    if ($[49] !== axis || $[50] !== axisProps || $[51] !== labelProps || $[52] !== xAxisOrientation || $[53] !== xAxisProps) {
      t13 = <XAxis key={axis} type="number" {...xAxisProps} {...axisProps} xAxisId={axis} orientation={xAxisOrientation} label={labelProps} />;
      $[49] = axis;
      $[50] = axisProps;
      $[51] = labelProps;
      $[52] = xAxisOrientation;
      $[53] = xAxisProps;
      $[54] = t13;
    } else {
      t13 = $[54];
    }
    return t13;
  } else {
    let t13;
    if ($[55] !== axis || $[56] !== axisProps || $[57] !== labelProps || $[58] !== yAxisOrientation || $[59] !== yAxisProps) {
      t13 = <YAxis key={axis} type="number" {...yAxisProps} {...axisProps} yAxisId={axis} orientation={yAxisOrientation} label={labelProps} />;
      $[55] = axis;
      $[56] = axisProps;
      $[57] = labelProps;
      $[58] = yAxisOrientation;
      $[59] = yAxisProps;
      $[60] = t13;
    } else {
      t13 = $[60];
    }
    return t13;
  }
};
export const SecondAxisNaked = t0 => {
  const $ = _c(12);
  const {
    id,
    layout,
    secondAxisDomain
  } = t0;
  const t1 = id as string;
  let t2;
  if ($[0] !== t1) {
    t2 = {
      xAxisId: t1
    };
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const xAxisProps = t2;
  const t3 = id as string;
  let t4;
  if ($[2] !== t3) {
    t4 = {
      yAxisId: t3
    };
    $[2] = t3;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const yAxisProps = t4;
  let t5;
  if ($[4] !== secondAxisDomain) {
    t5 = {
      scale: "auto",
      domain: secondAxisDomain,
      hide: true,
      type: "number"
    };
    $[4] = secondAxisDomain;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const axisProps = t5;
  let t6;
  if ($[6] !== axisProps || $[7] !== id || $[8] !== layout || $[9] !== xAxisProps || $[10] !== yAxisProps) {
    t6 = layout === "vertical" ? <XAxis key={id} {...xAxisProps} {...axisProps} /> : <YAxis key={id} {...yAxisProps} {...axisProps} />;
    $[6] = axisProps;
    $[7] = id;
    $[8] = layout;
    $[9] = xAxisProps;
    $[10] = yAxisProps;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  return t6;
};
export default SecondAxis;